import { useEffect, useState } from "react";
import { useNavigate, useParams, Navigate, useLocation } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";
import DuringNotes from "../components/DuringNotes";

const BLOCKER_KEYS = ["resources", "sales", "product", "team", "clarity", "execution", "other"];
const ROLE_I18N = {
  entrepreneur: "roles.founder",
  advisor: "roles.advisor",
  accelerator: "roles.accelerator",
  provider: "roles.provider",
  investor: "roles.fundManager",
  admin: "roles.admin",
};

function partyRoleLabel(person, t, fallbackKey) {
  const key = ROLE_I18N[person?.role];
  return key ? t(key) : t(fallbackKey);
}

function FiveList({ label, values, onChange, testid }) {
  const items = values.length ? values : [""];
  const update = (i, v) => {
    const next = [...items];
    next[i] = v;
    onChange(next.filter((x, idx) => idx < items.length));
  };
  const add = () => onChange([...items, ""]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div>
      <label>{label}</label>
      {items.map((val, i) => (
        <div className="tb-list-entry" key={i}>
          <input
            type="text"
            value={val}
            onChange={(e) => update(i, e.target.value)}
            data-testid={`${testid}-${i}`}
          />
          {items.length > 1 && (
            <button type="button" onClick={() => remove(i)} aria-label="remove">
              ×
            </button>
          )}
        </div>
      ))}
      {items.length < 5 && (
        <button type="button" className="tb-ghost-btn" style={{ padding: "6px 12px", fontSize: "0.8rem" }} onClick={add} data-testid={`${testid}-add`}>
          + Add
        </button>
      )}
    </div>
  );
}

function TemplateField({ q, value, onChange, readOnly, lang, t }) {
  const label = q?.label?.[lang] || q?.label?.en || q?.label?.fr || q?.key || "";
  const hint = q?.hint?.[lang] || q?.hint?.en || q?.hint?.fr || "";
  const optLabel = (opt) => {
    const key = `detail.blockers.${opt}`;
    const translated = t(key);
    return translated === key ? opt : translated;
  };
  if (readOnly) {
    const shown = Array.isArray(value) ? value.join(", ") : (value ?? "—");
    return (
      <div style={{ marginBottom: 14 }}>
        <label>{label}</label>
        <p style={{ margin: "4px 0 0", whiteSpace: "pre-wrap" }}>{shown || "—"}</p>
      </div>
    );
  }
  if (q.type === "scale_1_10") {
    return (
      <div style={{ marginBottom: 14 }}>
        <label>{label}</label>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span aria-hidden="true">😊</span>
          <HappinessMeter value={Number(value) || 0} onChange={onChange} />
        </div>
      </div>
    );
  }
  if (q.type === "single_choice") {
    return (
      <div style={{ marginBottom: 14 }}>
        <label>{label}</label>
        <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={`tpl-${q.key}`}>
          <option value="">—</option>
          {(q.options || []).map((opt) => <option key={opt} value={opt}>{optLabel(opt)}</option>)}
        </select>
      </div>
    );
  }
  if (q.type === "multi_choice" || q.type === "checklist") {
    const selected = Array.isArray(value) ? value : [];
    return (
      <div style={{ marginBottom: 14 }}>
        <label>{label}</label>
        {(q.options || []).map((opt) => (
          <label key={opt} style={{ display: "flex", gap: 8, fontWeight: 400, marginTop: 4 }}>
            <input
              type="checkbox"
              checked={selected.includes(opt)}
              onChange={() => onChange(selected.includes(opt) ? selected.filter((x) => x !== opt) : [...selected, opt])}
            />
            {optLabel(opt)}
          </label>
        ))}
      </div>
    );
  }
  const Tag = q.type === "short_text" ? "input" : "textarea";
  return (
    <div style={{ marginBottom: 14 }}>
      <label>{label}</label>
      {hint ? <p className="tb-muted" style={{ margin: "0 0 6px", fontSize: "0.82rem" }}>{hint}</p> : null}
      <Tag
        type={q.type === "short_text" ? "text" : undefined}
        rows={q.type === "short_text" ? undefined : 3}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`tpl-${q.key}`}
      />
    </div>
  );
}

function TemplatePreForm({ session, me, lang, t, pack, onSubmitted }) {
  const questions = pack?.before_questions || pack?.questions || session.template_before_questions || session.template_questions || [];
  const mine = (pack?.agendas || []).find((a) => a.user_id === me.id);
  const existing = session.pre_form;
  const canEdit = me.id === session.entrepreneur_id && !existing && !mine?.submitted_at;
  const [answers, setAnswers] = useState(mine?.answers || existing || {});
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (mine?.answers) setAnswers(mine.answers);
    else if (existing) setAnswers(existing);
  }, [mine?.submitted_at, existing]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    const missingScale = questions.find((q) => q.type === "scale_1_10" && q.required && (answers[q.key] == null || answers[q.key] === ""));
    if (missingScale) {
      setErr(t("detail.scaleRequired"));
      return;
    }
    setLoading(true); setErr("");
    try {
      await api.post(`/sessions/${session.id}/agenda`, { answers });
      onSubmitted();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setLoading(false); }
  };

  const display = existing || mine?.answers;
  if (display && !canEdit) {
    return (
      <div className="tb-block full" data-testid="pre-form-readonly">
        <h3>{t("detail.preTitle")}</h3>
        <p className="tb-help">{me.id === session.entrepreneur_id ? t("detail.preForEntrepreneur") : t("detail.preForAdvisor")}</p>
        <div className="tb-summary">
          {questions.map((q) => (
            <TemplateField key={q.key} q={q} lang={lang} t={t} value={display[q.key]} readOnly />
          ))}
        </div>
      </div>
    );
  }
  if (!canEdit) {
    return (
      <div className="tb-block full" data-testid="pre-form-pending">
        <h3>{t("detail.preTitle")}</h3>
        <p className="tb-help">{t("detail.preNotYet")}</p>
      </div>
    );
  }
  return (
    <form className="tb-block full" onSubmit={submit} data-testid="pre-form">
      <h3>{t("detail.preTitle")}</h3>
      <p className="tb-help">{t("detail.preForEntrepreneur")}</p>
      {questions.map((q) => (
        <TemplateField
          key={q.key}
          q={q}
          lang={lang}
          t={t}
          value={answers[q.key]}
          onChange={(v) => setAnswers((prev) => ({ ...prev, [q.key]: v }))}
        />
      ))}
      {err && <div className="tb-form-error">{err}</div>}
      <button type="submit" className="tb-primary-btn" disabled={loading} data-testid="pre-submit">
        {loading ? t("auth.loading") : t("detail.submitPre")}
      </button>
    </form>
  );
}

function TemplatePostForm({ session, me, lang, t, pack, onSubmitted }) {
  const questions = pack?.after_questions || session.template_after_questions || [];
  const mine = (pack?.agendas || []).find((a) => a.user_id === me.id);
  const existing = session.post_form;
  const canEdit = me.id === session.advisor_id && !existing && !mine?.after_submitted_at;
  const [answers, setAnswers] = useState(mine?.after_answers || existing || {});
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (mine?.after_answers) setAnswers(mine.after_answers);
    else if (existing) setAnswers(existing);
  }, [mine?.after_submitted_at, existing]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      await api.post(`/sessions/${session.id}/after-form`, { answers });
      onSubmitted();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setLoading(false); }
  };

  if (!questions.length) return null;
  const display = existing || mine?.after_answers;
  if (display && !canEdit) {
    return (
      <div className="tb-block" data-testid="post-form-readonly">
        <h3>{t("detail.postTitle")}</h3>
        <p className="tb-help">{t("detail.postForOther")}</p>
        <div className="tb-summary">
          {questions.map((q) => (
            <TemplateField key={q.key} q={q} lang={lang} t={t} value={display[q.key]} readOnly />
          ))}
        </div>
      </div>
    );
  }
  if (!canEdit) {
    return (
      <div className="tb-block" data-testid="post-form-pending">
        <h3>{t("detail.postTitle")}</h3>
        <p className="tb-help">{t("detail.postNotYet")}</p>
      </div>
    );
  }
  return (
    <form className="tb-block" onSubmit={submit} data-testid="post-form">
      <h3>{t("detail.postTitle")}</h3>
      <p className="tb-help">{t("detail.postForAdvisor")}</p>
      {questions.map((q) => (
        <TemplateField
          key={q.key}
          q={q}
          lang={lang}
          t={t}
          value={answers[q.key]}
          onChange={(v) => setAnswers((prev) => ({ ...prev, [q.key]: v }))}
        />
      ))}
      {err && <div className="tb-form-error">{err}</div>}
      <button type="submit" className="tb-primary-btn" disabled={loading} data-testid="post-submit">
        {loading ? t("auth.loading") : t("detail.submitPost")}
      </button>
    </form>
  );
}

function HappinessMeter({ value, onChange }) {
  return (
    <div className="tb-happy-meter" data-testid="happy-meter">
      {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
        <button
          type="button"
          key={n}
          className={value === n ? "sel" : ""}
          onClick={() => onChange(n)}
          data-testid={`happy-${n}`}
        >
          {n}
        </button>
      ))}
    </div>
  );
}

function PreForm({ session, me, lang, t, onSubmitted }) {
  const existing = session.pre_form;
  const canEdit = me.id === session.entrepreneur_id && !existing;
  const [form, setForm] = useState(
    existing || {
      happiness_level: null,
      happiness_reason: "",
      challenges: [""],
      opportunities: [""],
      priorities: [""],
      priority_30d: "",
      priority_30d_reason: "",
      limiting_factor: "",
      objective: "",
      blocker_category: "clarity",
      blocker_details: "",
    }
  );
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (form.happiness_level == null) {
      setErr(t("detail.scaleRequired"));
      return;
    }
    setLoading(true);
    setErr("");
    try {
      await api.put(`/sessions/${session.id}/preform`, {
        ...form,
        happiness_level: Number(form.happiness_level),
        challenges: form.challenges.filter((x) => x),
        opportunities: form.opportunities.filter((x) => x),
        priorities: form.priorities.filter((x) => x),
      });
      onSubmitted();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  if (existing) {
    // Read-only display
    return (
      <div className="tb-block full" data-testid="pre-form-readonly">
        <h3>{t("detail.preTitle")}</h3>
        <p className="tb-help">
          {me.id === session.entrepreneur_id ? t("detail.preForEntrepreneur") : t("detail.preForAdvisor")}
        </p>
        <div className="tb-summary">
          <h4>Happiness</h4>
          <p><b>{existing.happiness_level}/10</b> — {existing.happiness_reason || "—"}</p>
          <h4>{t("detail.challenges")}</h4>
          <ul>{(existing.challenges || []).map((c, i) => <li key={i}>{c}</li>)}</ul>
          <h4>{t("detail.opportunities")}</h4>
          <ul>{(existing.opportunities || []).map((c, i) => <li key={i}>{c}</li>)}</ul>
          <h4>{t("detail.priorities")}</h4>
          <ul>{(existing.priorities || []).map((c, i) => <li key={i}>{c}</li>)}</ul>
          <h4>{t("detail.priority30d")}</h4>
          <div className="tb-hero-field"><b>{existing.priority_30d || "—"}</b><div className="tb-muted" style={{ fontSize: "0.88rem", marginTop: 4 }}>{existing.priority_30d_reason}</div></div>
          <h4>{t("detail.limitingFactor")}</h4>
          <p>{existing.limiting_factor || "—"}</p>
          <h4>{t("detail.objective")}</h4>
          <p>{existing.objective || "—"}</p>
          <h4>{t("detail.blockerCategory")}</h4>
          <p><b>{t(`detail.blockers.${existing.blocker_category || "other"}`)}</b> — {existing.blocker_details}</p>
        </div>
      </div>
    );
  }

  if (!canEdit) {
    return (
      <div className="tb-block full" data-testid="pre-form-pending">
        <h3>{t("detail.preTitle")}</h3>
        <p className="tb-help">{t("detail.preNotYet")}</p>
      </div>
    );
  }

  return (
    <form className="tb-block full" onSubmit={submit} data-testid="pre-form">
      <h3>{t("detail.preTitle")}</h3>
      <p className="tb-help">{t("detail.preForEntrepreneur")}</p>
      <div>
        <label>{t("detail.happiness")}</label>
        <HappinessMeter value={form.happiness_level} onChange={(v) => set("happiness_level", v)} />
      </div>
      <div>
        <label>{t("detail.happinessReason")}</label>
        <textarea value={form.happiness_reason} onChange={(e) => set("happiness_reason", e.target.value)} data-testid="pre-happiness-reason" />
      </div>
      <FiveList label={t("detail.challenges")} values={form.challenges} onChange={(v) => set("challenges", v)} testid="pre-challenge" />
      <FiveList label={t("detail.opportunities")} values={form.opportunities} onChange={(v) => set("opportunities", v)} testid="pre-opportunity" />
      <FiveList label={t("detail.priorities")} values={form.priorities} onChange={(v) => set("priorities", v)} testid="pre-priority" />
      <div>
        <label>{t("detail.priority30d")}</label>
        <input type="text" value={form.priority_30d} onChange={(e) => set("priority_30d", e.target.value)} data-testid="pre-priority-30d" required />
      </div>
      <div>
        <label>{t("detail.priority30dReason")}</label>
        <textarea value={form.priority_30d_reason} onChange={(e) => set("priority_30d_reason", e.target.value)} data-testid="pre-priority-reason" />
      </div>
      <div>
        <label>{t("detail.limitingFactor")}</label>
        <input type="text" value={form.limiting_factor} onChange={(e) => set("limiting_factor", e.target.value)} data-testid="pre-limiting" />
      </div>
      <div>
        <label>{t("detail.objective")}</label>
        <textarea value={form.objective} onChange={(e) => set("objective", e.target.value)} data-testid="pre-objective" rows={3} />
      </div>
      <div>
        <label>{t("detail.blockerCategory")}</label>
        <select value={form.blocker_category} onChange={(e) => set("blocker_category", e.target.value)} data-testid="pre-blocker-cat">
          {BLOCKER_KEYS.map((k) => (
            <option key={k} value={k}>
              {t(`detail.blockers.${k}`)}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label>{t("detail.blockerDetails")}</label>
        <textarea value={form.blocker_details} onChange={(e) => set("blocker_details", e.target.value)} data-testid="pre-blocker-details" />
      </div>
      {err && <div className="tb-form-error">{err}</div>}
      <button type="submit" className="tb-primary-btn" disabled={loading} data-testid="pre-submit">
        {loading ? t("auth.loading") : t("detail.submitPre")}
      </button>
    </form>
  );
}

function PostForm({ session, me, t, onSubmitted }) {
  const existing = session.post_form;
  const canEdit = me.id === session.advisor_id && !existing;
  const [form, setForm] = useState(
    existing || {
      advisor_recommendations: [""],
      biggest_risk: "",
      objective_1: "",
      metric: "",
      owner: "",
      watchpoint: "",
      deadline: "",
      advisor_notes: "",
      transcript: "",
    }
  );
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const payload = {
        ...form,
        advisor_recommendations: form.advisor_recommendations.filter((x) => x),
      };
      if (payload.deadline) payload.deadline = new Date(payload.deadline).toISOString();
      else delete payload.deadline;
      await api.put(`/sessions/${session.id}/postform`, payload);
      onSubmitted();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  if (existing) {
    return (
      <div className="tb-block" data-testid="post-form-readonly">
        <h3>{t("detail.postTitle")}</h3>
        <p className="tb-help">{t("detail.postForOther")}</p>
        <div className="tb-summary">
          <h4>{t("detail.recommendations")}</h4>
          <ul>{(existing.advisor_recommendations || []).map((c, i) => <li key={i}>{c}</li>)}</ul>
          <h4>{t("detail.biggestRisk")}</h4>
          <p>{existing.biggest_risk || "—"}</p>
          <h4>{t("detail.objective1")}</h4>
          <div className="tb-hero-field"><b>{existing.objective_1 || "—"}</b></div>
          <h4>{t("detail.metric")}</h4>
          <p>{existing.metric || "—"}</p>
          <h4>{t("detail.owner")}</h4>
          <p>{existing.owner || "—"}</p>
          <h4>{t("detail.watchpoint")}</h4>
          <p>{existing.watchpoint || "—"}</p>
          {existing.advisor_notes && (<><h4>Notes</h4><p>{existing.advisor_notes}</p></>)}
        </div>
      </div>
    );
  }

  if (!canEdit) {
    return (
      <div className="tb-block" data-testid="post-form-pending">
        <h3>{t("detail.postTitle")}</h3>
        <p className="tb-help">{t("detail.postNotYet")}</p>
      </div>
    );
  }

  return (
    <form className="tb-block" onSubmit={submit} data-testid="post-form">
      <h3>{t("detail.postTitle")}</h3>
      <p className="tb-help">{t("detail.postForAdvisor")}</p>
      <FiveList
        label={t("detail.recommendations")}
        values={form.advisor_recommendations}
        onChange={(v) => set("advisor_recommendations", v)}
        testid="post-rec"
      />
      <div>
        <label>{t("detail.biggestRisk")}</label>
        <input type="text" value={form.biggest_risk} onChange={(e) => set("biggest_risk", e.target.value)} data-testid="post-risk" />
      </div>
      <div>
        <label>{t("detail.objective1")}</label>
        <input type="text" value={form.objective_1} onChange={(e) => set("objective_1", e.target.value)} data-testid="post-objective" required />
      </div>
      <div className="tb-row">
        <div>
          <label>{t("detail.metric")}</label>
          <input type="text" value={form.metric} onChange={(e) => set("metric", e.target.value)} data-testid="post-metric" />
        </div>
        <div>
          <label>{t("detail.owner")}</label>
          <input type="text" value={form.owner} onChange={(e) => set("owner", e.target.value)} data-testid="post-owner" />
        </div>
      </div>
      <div>
        <label>{t("detail.watchpoint")}</label>
        <input type="text" value={form.watchpoint} onChange={(e) => set("watchpoint", e.target.value)} data-testid="post-watchpoint" />
      </div>
      <div>
        <label>{t("detail.deadline")}</label>
        <input type="datetime-local" value={form.deadline} onChange={(e) => set("deadline", e.target.value)} data-testid="post-deadline" />
      </div>
      <div>
        <label>{t("detail.advisorNotes")}</label>
        <textarea value={form.advisor_notes} onChange={(e) => set("advisor_notes", e.target.value)} data-testid="post-notes" />
      </div>
      <div>
        <label>{t("detail.transcript")}</label>
        <small className="tb-muted" style={{ display: "block", marginBottom: 4 }}>{t("detail.transcriptHelp")}</small>
        <textarea value={form.transcript} onChange={(e) => set("transcript", e.target.value)} style={{ minHeight: 150 }} data-testid="post-transcript" />
      </div>
      {err && <div className="tb-form-error">{err}</div>}
      <button type="submit" className="tb-primary-btn" disabled={loading} data-testid="post-submit">
        {loading ? t("auth.loading") : t("detail.submitPost")}
      </button>
    </form>
  );
}

function SummaryBlock({ session, me, t, onRegenerated }) {
  const s = session.summary;
  const [loading, setLoading] = useState(false);
  const canRegen = me.id === session.advisor_id || me.role === "admin";

  const regen = async () => {
    setLoading(true);
    try {
      await api.post(`/sessions/${session.id}/regenerate-summary`);
      onRegenerated();
    } catch (e) {
      /* noop */
    } finally {
      setLoading(false);
    }
  };

  if (!s) {
    return (
      <div className="tb-block" data-testid="summary-empty">
        <h3>{t("detail.summaryTitle")}</h3>
        <p className="tb-help">{t("detail.summaryNotYet")}</p>
      </div>
    );
  }
  return (
    <div className="tb-block" data-testid="summary-block">
      <h3>{t("detail.summaryTitle")}</h3>
      <div className="tb-summary">
        <h4>General state</h4>
        <p>{s.general_state}</p>
        <h4>✓ Going well</h4>
        <ul>{(s.going_well || []).map((x, i) => <li key={i}>{x}</li>)}</ul>
        <h4>⚠ Blocked</h4>
        <ul>{(s.blocked || []).map((x, i) => <li key={i}>{x}</li>)}</ul>
        <h4>🎯 Priority #1</h4>
        <div className="tb-hero-field">
          <b>{s.priority_1?.objective || "—"}</b>
          <div className="tb-muted" style={{ fontSize: "0.88rem", marginTop: 4 }}>
            Metric: {s.priority_1?.metric || "—"} · Owner: {s.priority_1?.owner || "—"}
          </div>
        </div>
        <h4>Decisions</h4>
        <ul>{(s.decisions || []).map((x, i) => <li key={i}>{x}</li>)}</ul>
        <h4>Watchpoints</h4>
        <ul>{(s.watchpoints || []).map((x, i) => <li key={i}>{x}</li>)}</ul>
        <h4>Follow-up actions</h4>
        <ul>{(s.follow_up_actions || []).map((x, i) => <li key={i}>{x}</li>)}</ul>
      </div>
      {canRegen && (
        <button className="tb-ghost-btn" onClick={regen} disabled={loading} data-testid="regen-summary" style={{ marginTop: 12 }}>
          {loading ? t("auth.loading") : t("detail.regenerate")}
        </button>
      )}
    </div>
  );
}

function MeetingLinkCtl({ session, me, t, onUpdated }) {
  const canEdit = me.id === session.advisor_id || me.id === session.entrepreneur_id || me.role === "admin";
  const [editing, setEditing] = useState(false);
  const [link, setLink] = useState(session.meeting_link || "");
  const save = async () => {
    try {
      await api.put(`/sessions/${session.id}/meeting-link`, { meeting_link: link });
      setEditing(false);
      onUpdated();
    } catch (e) {
      /* noop */
    }
  };
  if (session.meeting_link && !editing) {
    return (
      <div className="tb-meeting-link-box" data-testid="meeting-link-box">
        <div className="tb-meeting-link-url" title={session.meeting_link}>
          <span aria-hidden="true">🔗</span>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{session.meeting_link}</span>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <a href={session.meeting_link} target="_blank" rel="noopener noreferrer" className="tb-primary-btn" style={{ textDecoration: "none", padding: "8px 16px" }} data-testid="join-meeting">
            ▶ {t("detail.joinMeeting")}
          </a>
          {canEdit && (
            <button className="tb-ghost-btn" onClick={() => setEditing(true)} data-testid="edit-meeting-link" style={{ padding: "8px 14px" }}>
              {t("detail.updateMeetingLink")}
            </button>
          )}
        </div>
      </div>
    );
  }
  if (!canEdit) return <span className="tb-muted">—</span>;
  return (
    <div className="tb-meeting-link-box" data-testid="meeting-link-box-edit">
      <small className="tb-muted" style={{ display: "block", marginBottom: 4 }}>{t("detail.linkHelp")}</small>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <input
          type="url"
          value={link}
          onChange={(e) => setLink(e.target.value)}
          placeholder={t("detail.pasteLinkPlaceholder")}
          style={{ flex: 1, minWidth: 240, padding: "8px 12px", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", fontFamily: "var(--font-body)" }}
          data-testid="meeting-link-input"
        />
        <button className="tb-primary-btn" onClick={save} style={{ padding: "8px 16px" }} data-testid="meeting-link-save">
          {t("detail.save")}
        </button>
        {link && (link.startsWith("http")) && (
          <a href={link} target="_blank" rel="noopener noreferrer" className="tb-ghost-btn" style={{ textDecoration: "none", padding: "8px 14px" }} data-testid="join-meeting-edit">
            ▶ {t("detail.joinMeeting")}
          </a>
        )}
      </div>
    </div>
  );
}

function SessionTerms({ session, me, t, lang, onUpdated }) {
  const isParty = me.id === session.advisor_id || me.id === session.entrepreneur_id;
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const isoLocal = (iso) => {
    const d = new Date(iso);
    const tz = d.getTimezoneOffset();
    return new Date(d.getTime() - tz * 60000).toISOString().slice(0, 16);
  };
  const [form, setForm] = useState({
    scheduled_at: isoLocal(session.scheduled_at),
    duration_min: session.duration_min,
    rate_amount: ((session.rate_cents || 0) / 100).toString(),
    rate_currency: session.rate_currency || "CAD",
  });

  const confirmed = session.confirmed_by || [];
  const advisorOk = confirmed.includes(session.advisor_id);
  const entOk = confirmed.includes(session.entrepreneur_id);
  const meOk = confirmed.includes(me.id);
  const bothOk = advisorOk && entOk;

  const fmtMoney = () => {
    const cents = session.rate_cents || 0;
    return new Intl.NumberFormat(lang === "fr" ? "fr-CA" : "en-CA", {
      style: "currency",
      currency: session.rate_currency || "CAD",
    }).format(cents / 100);
  };

  const confirm = async () => {
    setBusy(true); setErr("");
    try { await api.post(`/sessions/${session.id}/confirm`); onUpdated(); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const propose = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.put(`/sessions/${session.id}/terms`, {
        scheduled_at: new Date(form.scheduled_at).toISOString(),
        duration_min: Number(form.duration_min) || 60,
        rate_cents: Math.round((Number(form.rate_amount) || 0) * 100),
        rate_currency: form.rate_currency,
      });
      setEditing(false); onUpdated();
    } catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    const reason = window.prompt(t("terms.cancelPromptReason") || "Reason (optional):", "");
    if (reason === null) return; // user dismissed
    if (!window.confirm(t("terms.cancelConfirm"))) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/sessions/${session.id}/cancel`, { reason });
      onUpdated();
    } catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };

  const otherPartyId = me.id === session.advisor_id ? session.entrepreneur_id : session.advisor_id;
  const otherConfirmed = confirmed.includes(otherPartyId);
  const canHardDelete = (
    me.role === "admin" ||
    (me.id === session.created_by && !otherConfirmed && session.status !== "cancelled" && session.status !== "completed") ||
    session.status === "cancelled" ||
    session.status === "completed"
  );
  const archivedBy = session.archived_by || [];
  const isArchived = archivedBy.includes(me.id);

  const hardDelete = async () => {
    if (!window.confirm(t("terms.deleteConfirm"))) return;
    setBusy(true); setErr("");
    try {
      await api.delete(`/sessions/${session.id}`);
      window.location.href = "/app/sessions";
    } catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message); setBusy(false); }
  };

  const archive = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/sessions/${session.id}/${isArchived ? "unarchive" : "archive"}`);
      onUpdated();
    } catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };

  const resendInvite = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/sessions/${session.id}/resend-invite`);
      onUpdated();
    } catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="tb-block full" data-testid="session-terms">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <h3 style={{ margin: 0, fontFamily: "var(--font-display)" }}>{t("terms.title")}</h3>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span className={`tb-badge ${bothOk ? "good" : "warn"}`} data-testid="terms-status">
            {bothOk ? `✓ ${t("terms.bothConfirmed")}` : t("terms.pending")}
          </span>
          <span className="tb-muted" style={{ fontSize: "0.78rem" }}>
            {partyRoleLabel(session.advisor, t, "terms.advisor")}: {advisorOk ? "✓" : "…"} · {partyRoleLabel(session.entrepreneur, t, "terms.entrepreneur")}: {entOk ? "✓" : "…"}
          </span>
        </div>
      </div>

      {!editing && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginTop: 12 }}>
          <div><div className="tb-muted" style={{ fontSize: "0.75rem" }}>{t("terms.when")}</div>
            <b data-testid="terms-when">{new Date(session.scheduled_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-US", { dateStyle: "medium", timeStyle: "short" })}</b></div>
          <div><div className="tb-muted" style={{ fontSize: "0.75rem" }}>{t("terms.duration")}</div>
            <b data-testid="terms-duration">{session.duration_min} min</b></div>
          {(session.rate_cents || 0) > 0 && (
          <div><div className="tb-muted" style={{ fontSize: "0.75rem" }}>{t("terms.rate")}</div>
            <b data-testid="terms-rate">{fmtMoney()}</b></div>
          )}
        </div>
      )}

      {editing && (
        <form onSubmit={propose} style={{ marginTop: 12 }}>
          <div className="tb-row">
            <div><label>{t("terms.when")}</label>
              <input type="datetime-local" required value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} data-testid="terms-edit-when" /></div>
            <div><label>{t("terms.duration")}</label>
              <input type="number" min="15" max="180" step="15" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} data-testid="terms-edit-duration" /></div>
          </div>
          {(session.rate_cents || 0) > 0 && (
          <div className="tb-row">
            <div><label>{t("book.rate")}</label>
              <input type="number" min="0" step="1" value={form.rate_amount} onChange={(e) => setForm({ ...form, rate_amount: e.target.value })} data-testid="terms-edit-rate" /></div>
            <div><label>{t("book.currency")}</label>
              <select value={form.rate_currency} onChange={(e) => setForm({ ...form, rate_currency: e.target.value })} data-testid="terms-edit-currency">
                <option value="CAD">CAD</option><option value="USD">USD</option><option value="EUR">EUR</option>
              </select></div>
          </div>
          )}
          {err && <div className="tb-form-error">{err}</div>}
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button type="submit" className="tb-primary-btn" disabled={busy} data-testid="terms-submit">{t("terms.propose")}</button>
            <button type="button" className="tb-ghost-btn" onClick={() => setEditing(false)}>{t("book.cancel")}</button>
          </div>
        </form>
      )}

      {!editing && isParty && (
        <>
          <div className="tb-action-row" style={{ marginTop: 14 }} data-testid="terms-actions">
            {session.status !== "cancelled" && !meOk && (
              <button className="tb-primary-btn" onClick={confirm} disabled={busy} data-testid="terms-confirm">
                {t("terms.confirm")}
              </button>
            )}
            {session.status !== "cancelled" && (
              <button className="tb-ghost-btn" onClick={() => setEditing(true)} disabled={busy} data-testid="terms-edit">
                {t("terms.proposeChanges")}
              </button>
            )}
            {session.status !== "cancelled" && session.status !== "completed" && (
              <button className="tb-danger-btn" onClick={cancel} disabled={busy} data-testid="terms-cancel">
                {t("terms.cancelMeeting")}
              </button>
            )}
            {canHardDelete && (
              <button className="tb-danger-btn" onClick={hardDelete} disabled={busy} data-testid="terms-delete" style={{ background: "rgba(220,70,70,0.18)", color: "#ff8080" }}>
                {t("terms.deleteMeeting")}
              </button>
            )}
            <button className="tb-ghost-btn" onClick={archive} disabled={busy} data-testid="terms-archive">
              {isArchived ? t("terms.unarchive") : t("terms.archive")}
            </button>
            {session.status !== "cancelled" && session.status !== "completed" && (
              <button type="button" className="tb-ghost-btn" onClick={resendInvite} disabled={busy} data-testid="terms-resend-invite">
                {t("terms.resendInvite")}
              </button>
            )}
            {session.status === "cancelled" && (
              <span className="tb-badge bad" data-testid="terms-cancelled-badge" style={{ marginLeft: "auto" }}>
                {t("terms.cancelled")}{session.cancel_reason ? ` — ${session.cancel_reason}` : ""}
              </span>
            )}
          </div>
          {err && <div className="tb-form-error" style={{ marginTop: 10 }}>{err}</div>}
        </>
      )}
    </div>
  );
}

export default function SessionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backTo = (location.state && location.state.from) || "/app/bookings";
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [session, setSession] = useState(null);
  const [pack, setPack] = useState(null);
  const [err, setErr] = useState("");

  const load = () => {
    api
      .get(`/sessions/${id}`)
      .then((r) => setSession(r.data))
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
    api.get(`/sessions/${id}/agendas`).then((r) => setPack(r.data)).catch(() => setPack(null));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (err) {
    return (
      <div className="tb-page">
        <button className="tb-ghost-btn" onClick={() => navigate("/app")}>← {t("detail.back")}</button>
        <div className="tb-form-error" style={{ marginTop: 16 }}>{err}</div>
      </div>
    );
  }
  if (!session) return <div className="tb-loading">{t("common.loading")}</div>;
  if (session.type === "internal_1_1") {
    return <Navigate to={`/app/team-1-1/${session.id}`} replace />;
  }

  const dt = new Date(session.scheduled_at);
  const usesTemplate = !!(session.template_id || (session.template_before_questions || []).length);

  return (
    <div className="tb-page">
      <button className="tb-ghost-btn" onClick={() => navigate(backTo)} style={{ marginBottom: 16 }} data-testid="back-btn">
        ← {t("detail.back")}
      </button>
      <div className="tb-detail-head">
        <h1 className="tb-h1" style={{ margin: 0 }} data-testid="session-title">
          {session.title || "Advisory session"}
        </h1>
        <div className="tb-detail-meta">
          <span><b>{dt.toLocaleString(lang === "fr" ? "fr-CA" : "en-US", { weekday: "long", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" })}</b></span>
          <span>{session.duration_min} min</span>
          <span>
            <b>{session.advisor?.name}</b> ({partyRoleLabel(session.advisor, t, "book.advisor")}) · <b>{session.entrepreneur?.name}</b> ({partyRoleLabel(session.entrepreneur, t, "book.entrepreneur")})
          </span>
          {session.template_name && session.template_name !== session.title && (
            <span data-testid="session-template-name">{session.template_name}</span>
          )}
        </div>
        <div style={{ marginTop: 12 }}>
          <MeetingLinkCtl session={session} me={user} t={t} onUpdated={load} />
        </div>
      </div>

      <div className="tb-session-stack">
        <SessionTerms session={session} me={user} t={t} lang={lang} onUpdated={load} />

        {usesTemplate ? (
          <TemplatePreForm session={session} me={user} lang={lang} t={t} pack={pack} onSubmitted={load} />
        ) : (
          <PreForm session={session} me={user} lang={lang} t={t} onSubmitted={load} />
        )}

        <DuringNotes
          sessionId={id}
          user={user}
          t={t}
          lang={lang}
          prompts={session.template_during_note_prompts || pack?.during_note_prompts || []}
          readOnly={session.viewer_access === "shared_reader"}
        />

        {usesTemplate ? (
          <TemplatePostForm session={session} me={user} lang={lang} t={t} pack={pack} onSubmitted={load} />
        ) : (
          <PostForm session={session} me={user} t={t} onSubmitted={load} />
        )}
        <SummaryBlock session={session} me={user} t={t} onRegenerated={load} />
        <TranscriptBlock session={session} t={t} />
      </div>
    </div>
  );
}

function TranscriptBlock({ session, t }) {
  const [open, setOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryErr, setRetryErr] = useState("");
  const segs = session.transcript_segments || [];
  const status = session.transcript_status;
  const recallStatus = session.recall_status;
  const recallError = session.recall_error;
  const hasMeetingLink = !!(session.meeting_link || session.meet_url);
  // Show block if we have any pipeline state OR a meeting link exists (so we can show "not scheduled" instead of hiding).
  if (!status && !recallStatus && !hasMeetingLink) return null;

  const refresh = async () => {
    setRefreshing(true);
    try {
      await api.post(`/sessions/${session.id}/refresh-transcript`);
      window.location.reload();
    } catch (e) {
      setRefreshing(false);
    }
  };

  const retryDispatch = async () => {
    setRetrying(true);
    setRetryErr("");
    try {
      const r = await api.post(`/sessions/${session.id}/dispatch-recall`);
      if (r.data?.ok) {
        window.location.reload();
      } else {
        setRetryErr(r.data?.recall_error || t("detail.recall.retryFailed"));
        setRetrying(false);
      }
    } catch (e) {
      setRetryErr(e.response?.data?.detail || e.message);
      setRetrying(false);
    }
  };

  const fmtTime = (sec) => {
    const s = Math.max(0, Math.floor(sec || 0));
    const m = Math.floor(s / 60); const r = s % 60;
    return `${m}:${String(r).padStart(2, "0")}`;
  };
  const fullText = segs.map((x) => `[${fmtTime(x.start)}] ${x.speaker || "Speaker"}: ${x.text}`).join("\n");

  const download = (ext) => {
    const blob = new Blob([fullText], { type: ext === "md" ? "text/markdown" : "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `transcript-${session.id}.${ext}`; a.click();
    URL.revokeObjectURL(url);
  };

  // Only genuine dispatch/attendance failures should surface the banner — NOT the
  // successful terminal states Recall reports after a good meeting (done/completed/
  // call_ended/recording_done) and NOT in-progress states. Never show it once a
  // transcript is available.
  const RECALL_FAIL_STATES = ["failed", "fatal", "error", "not_configured", "skipped_optout", "no_meeting_url"];
  const dispatchFailed = status !== "available" && (RECALL_FAIL_STATES.includes(recallStatus) || (!!recallError && recallStatus !== "scheduled"));
  const badgeClass = status === "available" ? "good" : (dispatchFailed ? "bad" : "muted");
  const badgeText = t(`detail.transcriptStatus.${status || recallStatus || "pending"}`);

  return (
    <div className="tb-block tb-section-follow" data-testid="transcript-block">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <h3 style={{ marginTop: 0 }}>{t("detail.fullTranscript")}</h3>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span className={`tb-badge ${badgeClass}`} data-testid="transcript-status-badge">
            {badgeText}
          </span>
          {status !== "available" && !dispatchFailed && (
            <button type="button" className="tb-ghost-btn" onClick={refresh} disabled={refreshing} data-testid="transcript-refresh" style={{ fontSize: "0.78rem", padding: "4px 10px" }}>
              {refreshing ? "…" : t("detail.refreshTranscript")}
            </button>
          )}
          {dispatchFailed && hasMeetingLink && (
            <button type="button" className="tb-primary-btn tb-mini" onClick={retryDispatch} disabled={retrying} data-testid="recall-retry-btn">
              {retrying ? "…" : t("detail.recall.retryScheduleBot")}
            </button>
          )}
        </div>
      </div>
      {dispatchFailed && (
        <div className="tb-form-error" data-testid="recall-error-banner" style={{ marginTop: 8 }}>
          <strong>{t("detail.recall.notAttendingTitle")}</strong>
          <div style={{ marginTop: 4, fontSize: "0.85rem" }}>
            {recallStatus === "not_configured"
              ? t("detail.recall.notConfigured")
              : recallStatus === "skipped_optout"
                ? t("detail.recall.skippedOptout")
                : recallStatus === "no_meeting_url"
                  ? t("detail.recall.noMeetingUrl")
                  : t("detail.recall.dispatchFailed")}
          </div>
          {recallError && (
            <details style={{ marginTop: 6, fontSize: "0.8rem" }}>
              <summary style={{ cursor: "pointer" }}>{t("detail.recall.showDetails")}</summary>
              <pre style={{ whiteSpace: "pre-wrap", marginTop: 4, opacity: 0.85 }}>{recallError}</pre>
            </details>
          )}
          {retryErr && <div style={{ marginTop: 6, color: "#ff8e8e", fontSize: "0.8rem" }}>{retryErr}</div>}
        </div>
      )}
      {!dispatchFailed && segs.length === 0 && (
        <p className="tb-muted" style={{ marginTop: 0 }}>{t("detail.transcriptPending")}</p>
      )}
      {segs.length > 0 && (
        <>
          <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
            <button type="button" className="tb-ghost-btn" onClick={() => setOpen(!open)} data-testid="transcript-toggle">
              {open ? t("detail.hide") : t("detail.show")} ({segs.length})
            </button>
            <button type="button" className="tb-ghost-btn" onClick={() => download("txt")}>↓ .txt</button>
            <button type="button" className="tb-ghost-btn" onClick={() => download("md")}>↓ .md</button>
          </div>
          {open && (
            <div style={{ maxHeight: 480, overflowY: "auto", paddingRight: 8, borderTop: "1px solid rgba(255,255,255,0.06)" }} data-testid="transcript-content">
              {segs.map((x, i) => (
                <div key={i} style={{ marginTop: 10 }}>
                  <div className="tb-muted" style={{ fontSize: "0.78rem" }}>
                    [{fmtTime(x.start)}] <strong>{x.speaker || "Speaker"}</strong>
                  </div>
                  <div style={{ fontSize: "0.9rem", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>{x.text}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
